import { useState, useEffect, useContext } from "react";
import { LanguageContext } from "../../../constant/LanguageContext";
import { apiFetch } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { getTrimmedCompanyContext, encodePathSegment } from "../utils/encodeUtils";
import { translations } from "../constants/translations";

const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/api`;

export const useAdminLevels = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];
  const { profile } = useProfile();

  const [adminLevels, setAdminLevels]       = useState([]);
  const [companies, setCompanies]           = useState([]);
  const [selectedCompany, setSelectedCompany] = useState({ client: "", company: "" });
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [success, setSuccess]               = useState(null);

  // auto-clear messages
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(null), 5000); return () => clearTimeout(t); }
  }, [error]);

  // resolve client from profile or localStorage
  useEffect(() => {
    const client =
      profile?.clientName ||
      localStorage.getItem("clientName") ||
      localStorage.getItem("client") || "";
    if (client) setSelectedCompany((prev) => ({ ...prev, client: String(client).trim() }));
  }, [profile]);

  // fetch companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setCompaniesLoading(true);
        const res  = await apiFetch(`${API_BASE_URL}/company-config/all`, { method: "GET" });
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.data || data.companies || data.companyConfigs || []);
        const trimmedClient = String(selectedCompany.client).trim();
        setCompanies(
          trimmedClient
            ? list.filter((c) => String(c?.clientName || "").trim() === trimmedClient)
            : list
        );
      } catch (err) {
        setError("Failed to load companies");
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };
    fetchCompanies();
  }, [selectedCompany.client]);

  const fetchAdminLevels = async (clientName, companyName) => {
    const { client, company } = getTrimmedCompanyContext(clientName, companyName);
    if (!client || !company) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await apiFetch(
        `${API_BASE_URL}/admin-levels/company/${encodePathSegment(client)}/${encodePathSegment(company)}`,
        { method: "GET" }
      );
      const data = await res.json();
      setAdminLevels(data.adminLevels || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshUserAdminLevel = async () => {
    try {
      const userId = profile?.userId || localStorage.getItem("userId");
      if (!userId) return;
      const res  = await apiFetch(`${API_BASE_URL}/users/${userId}/admin-level`, { method: "GET" });
      const data = await res.json();
      if (data) {
        localStorage.setItem("adminLevel", JSON.stringify(data));
        // ✅ Dispatch permission refresh event so all listeners (AuthContext, etc) update
        window.dispatchEvent(new Event("storage"));
      }
    } catch (_) {}
  };

  const createAdminLevel = async (formData) => {
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`${API_BASE_URL}/admin-levels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, clientName: client, workingCompany: company }),
      });
      setSuccess(t.created);
      fetchAdminLevels(client, company);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteAdminLevel = async (levelId) => {
    setError(null);
    try {
      await apiFetch(`${API_BASE_URL}/admin-levels/${levelId}`, { method: "DELETE" });
      setSuccess(t.deleted);
      const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
      fetchAdminLevels(client, company);
    } catch (err) {
      setError(err.message);
    }
  };

  const setModulePermission = async (levelId, module, isEnabled) => {
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    try {
      await apiFetch(`${API_BASE_URL}/admin-levels/${levelId}/modules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ module, workingCompany: company, isEnabled }),
      });
      setSuccess(t.moduleUpdated);
      fetchAdminLevels(client, company);
      await refreshUserAdminLevel();
    } catch (err) {
      setError(err.message);
    }
  };

  const setFeaturePermission = async (modulePermissionId, featureName, isEnabled) => {
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    try {
      if (isEnabled === true) {
        // Enable: POST to create/enable feature
        await apiFetch(`${API_BASE_URL}/admin-levels/modules/${modulePermissionId}/features`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ featureName, isEnabled: true }),
        });
      } else {
        // Disable: PUT to disable without deleting
        await apiFetch(`${API_BASE_URL}/admin-levels/modules/${modulePermissionId}/features/${encodeURIComponent(featureName)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ featureName, isEnabled: false }),
        });
      }
      setSuccess(t.featureUpdated);
      fetchAdminLevels(client, company);
      await refreshUserAdminLevel();
    } catch (err) {
      setError(err.message);
    }
  };

  // One refresh for the whole batch — bulk callers (select-all) pass
  // silent:true per item and call this once at the end, instead of
  // refetching the levels list after every single request.
  const refreshLevels = async () => {
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    fetchAdminLevels(client, company);
    await refreshUserAdminLevel();
  };

  // Returns the created/updated modulePermission row (or null on failure) so
  // bulk flows can chain feature-adds without waiting for a list refetch.
  const addModule = async (levelId, moduleName, { silent = false } = {}) => {
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/${levelId}/modules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ module: moduleName, workingCompany: company, isEnabled: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) throw new Error(data?.error || `Request failed (${res.status})`);
      if (!silent) {
        setSuccess(t.moduleAdded);
        fetchAdminLevels(client, company);
        await refreshUserAdminLevel();
      }
      return data.permission || null;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const addFeature = async (modulePermissionId, featureName, { silent = false } = {}) => {
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/modules/${modulePermissionId}/features`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ featureName, isEnabled: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) throw new Error(data?.error || `Request failed (${res.status})`);
      if (!silent) {
        setSuccess(t.featureAdded);
        fetchAdminLevels(client, company);
        await refreshUserAdminLevel();
      }
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  return {
    adminLevels, companies, selectedCompany, setSelectedCompany,
    companiesLoading, loading, error, success,
    fetchAdminLevels, createAdminLevel, deleteAdminLevel,
    setModulePermission, setFeaturePermission, addModule, addFeature,
    refreshLevels, setSuccess, setError,
  };
};